// Shared ETA logic for the import progress UIs (in-report panel + listing pill)
// so they always agree. The raw estimate (avg time per completed batch ×
// remaining batches) is noisy early on and drifts upward every second between
// polls, so instead of a single exact figure we:
//   - withhold it until there is enough data (>= 3 completed batches),
//   - express it as a rounded range ("about 45–50 min") whose bucket widens with
//     the size of the estimate, so small poll-to-poll wobble no longer shows.
// Callers freeze the inputs at poll time (not per second) so the range is stable
// between polls.

const MIN_BATCHES = 3;

// Returns one of:
//   null                       -> not enough data yet (show "Estimating…")
//   { finishing: true }        -> no batches remaining
//   { lessThanMinute: true }   -> under a minute left
//   { lowMin, highMin }        -> a rounded minute range (lowMin may be < highMin)
export function etaParts({ completedBatches, totalBatches, elapsedMs }) {
  if (!completedBatches || !totalBatches || !(elapsedMs > 0)) return null;

  const remaining = totalBatches - completedBatches;
  if (remaining <= 0) return { finishing: true };

  // Tiny imports never reach MIN_BATCHES; show something once past the first.
  const minBatches = totalBatches <= MIN_BATCHES ? 1 : MIN_BATCHES;
  if (completedBatches < minBatches) return null;

  const pointMin = ((elapsedMs / completedBatches) * remaining) / 60000;
  if (pointMin < 1) return { lessThanMinute: true };

  // Coarser buckets for larger estimates: ±1 min under 10, ±5 under 45, ±10 above.
  const g = pointMin < 10 ? 1 : pointMin < 45 ? 5 : 10;
  const lowMin = Math.max(1, Math.floor(pointMin / g) * g);
  let highMin = Math.ceil(pointMin / g) * g;
  if (highMin <= lowMin) highMin = lowMin + g;

  return { lowMin, highMin };
}

// Build the display string from etaParts(). `t` is a vue-i18n $t function.
// Returns '' when there is nothing to show (caller decides whether to show a
// placeholder like "Estimating…").
export function etaLabelFromParts(parts, t) {
  if (!parts) return '';
  if (parts.finishing) return t('deforestation.importEtaFinishing');
  if (parts.lessThanMinute) return t('deforestation.importEtaLessThanMinute');
  return t('deforestation.importEtaRange', { low: parts.lowMin, high: parts.highMin });
}
